import React, { FC, useCallback, useMemo, useState } from 'react';
import {PaginationMetaInterface, ServiceProviderInterface, StudentFilter, StudentInterface, StudentStatus} from '../interfaces';
import { useResponseHandlerService } from './response-handler.service';
import { useTranslation } from './translation.service';
import { useAuth0 } from '@auth0/auth0-react';

const apiUrl = process.env.REACT_APP_API_URL || '';
const serviceRootURL = `${apiUrl}/api/student`;
const headers = {
	'Content-Type': 'application/json',
	Authorization: '',
};

interface StudentServiceInterface {
	meta: PaginationMetaInterface;
	students: StudentInterface[];
	getStudents: (filter: StudentFilter) => Promise<void>;
	deleteStudent: (id: string, notes?: string) => Promise<void>;
	updateStudent: (id: string, data: StudentInterface) => Promise<void>;
	createStudent: (student: StudentInterface, requestId: string) => Promise<void>;
	updateStudentsStatusByRequestId: (requestId: string, status: StudentStatus, oldStatus: StudentStatus) => Promise<void>;
}

interface StudentProviderInterface extends ServiceProviderInterface {}

const { createContext, useContext } = React;

const StudentContext = createContext<StudentServiceInterface>(
	{} as StudentServiceInterface
);

export const StudentProvider: FC<StudentProviderInterface> = (props) => {
	const { getIdTokenClaims } = useAuth0();

	const { addSuccess } = useResponseHandlerService();
	const { t } = useTranslation();
	const [students, setStudents] = useState<StudentInterface[]>([]);
	const [meta, setMeta] = useState({} as any);
	const getStudents = useCallback(
		async (filter: StudentFilter) => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';
			let res;
			try {
				res =  await getStudentsApiCall(filter);
			} catch(e) {}
			if (res) {
				if (filter.limit) {
					const {items, meta} = res as {items: StudentInterface[], meta: PaginationMetaInterface };
					setStudents(items);
					setMeta(meta);
				} else {
					setStudents([...students, ...res as StudentInterface[]]);
				}
			} else {
				setStudents([]);
			}
		},
		[students, setStudents, getIdTokenClaims]
	);

	const updateStudent = useCallback(
		async (id: string, data: StudentInterface) => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';

			const res = await updateStudentApiCall(id, data);
			if (res) {
				addSuccess(t('Student was successfully updated.'));
				const index = students.findIndex((student) => student.id === res.id);
				const updatedStudents = [...students];
				updatedStudents[index] = {
					...updatedStudents[index],
					...res
				};
				setStudents(updatedStudents);
			}
		},
		[addSuccess, t, students, getIdTokenClaims]
	);

	const updateStudentsStatusByRequestId = useCallback(
		async (requestId: string, status: StudentStatus, oldStatus: StudentStatus) => {
			if (!students || students.length === 0) {
				return;
			}
			let studentsFromRequest = students.filter((student) => student.requestId === requestId && student.status === oldStatus);
			if (studentsFromRequest.length === 0) {
				return;
			}

			studentsFromRequest = studentsFromRequest.map(student => {
				student.status = status;
				return student;
			});
			setStudents(students);
		},
		[students]
	);

	const createStudent = useCallback(
		async (student: StudentInterface, requestId: string) => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';

			const res = await createStudentApiCall(student, requestId);
			if (res) {
				addSuccess(t('Student was successfully created.'));
				setStudents([...students, res]);
			}
		},
		[addSuccess, t, students, getIdTokenClaims]
	);

	const deleteStudent = useCallback(
		async (id: string, notes = ''): Promise<void> => {
			const user = await getIdTokenClaims();
			headers.Authorization = user?.__raw || '';

			return deleteStudentAPICall(id, notes).then(() => {
				addSuccess(t('Student was successfully removed.'));
				setStudents(students.filter((student) => student.id !== id));
			});
		},
		[addSuccess, t, students, getIdTokenClaims]
	);

	const value = useMemo(
		() => ({
			meta,
			students,
			getStudents,
			deleteStudent,
			updateStudent,
			createStudent,
			updateStudentsStatusByRequestId
		}),
		[students, meta]
	);

	return (
		<StudentContext.Provider value={value}>{props.children}</StudentContext.Provider>
	);
};

export const useStudentService = () => {
	return useContext<StudentServiceInterface>(StudentContext);
};

const getStudentsApiCall = async (filter?: any): Promise<StudentInterface[]| {items: StudentInterface[], meta: PaginationMetaInterface}> => {
	const params = new URLSearchParams(filter).toString();
	const res = await window.fetch(`${serviceRootURL}?${params}`, { headers });
	return res.json();
};

const deleteStudentAPICall = async (id: string, notes = ''): Promise<void> => {
	await window.fetch(`${serviceRootURL}/${id}`, {
		method: 'DELETE',
		headers,
		body: JSON.stringify({ notes }),
	});
};

const updateStudentApiCall = async (
	id: string,
	student: StudentInterface
): Promise<StudentInterface> => {
	try {
		const { id: i, ...data } = student;
		const res = await window.fetch(`${serviceRootURL}/${id}`, {
			method: 'PUT',
			headers,
			body: JSON.stringify(data),
		});

		return res.json();
	} catch (e) {
		console.error(e);
		throw e;
	}
};

const createStudentApiCall = async (
	student: StudentInterface,
	requestId: string
): Promise<StudentInterface> => {
	try {
		const { id: i, ...data } = student;
		const res = await window.fetch(`${serviceRootURL}/request/${requestId}`, {
			method: 'POST',
			headers,
			body: JSON.stringify(data),
		});
		return res.json();
	} catch (e) {
		console.error(e);
		throw e;
	}
};
